/**
 * HxCallParticipants Component
 *
 * A dynamic panel listing all call participants with their audio/video status, speaking indicator, connection quality,
 * and host badge. Updates in real-time as participants join or leave via signal inputs.
 *
 * @example
 *   ```html
 *   <hx-call-participants
 *     [participants]="participants()"
 *     layout="list"
 *   />
 *   ```;
 *
 * @example
 *   Grid layout for video thumbnails
 *   ```html
 *   <hx-call-participants
 *   [participants]="participants()"
 *   layout="grid"
 *   [showConnectionQuality]="true"
 *   />
 *   ```
 *
 * @package @complexcare/harmony
 */

import { ChangeDetectionStrategy, Component, computed, input, output, ViewEncapsulation } from '@angular/core';
import { HxAvatar } from '../avatar/avatar';
import { HxButton } from '../button/button';
import { HxConnectionQuality } from '../connection-quality/connection-quality';
import { HxIcon } from '../icon/icon';
import { CallParticipant, CallParticipantsLayout } from './call-participants.types';

@Component({
  selector: 'hx-call-participants',
  standalone: true,
  imports: [HxAvatar, HxButton, HxConnectionQuality, HxIcon],
  templateUrl: './call-participants.html',
  styleUrls: ['./call-participants.css'],
  host: {
    class: 'hx-call-participants',
    '[class.hx-call-participants--grid]': 'layout() === "grid"',
    role: 'region',
    'aria-label': 'Participants',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxCallParticipants {
  /** Live list of participants – update the array reference to trigger re-render */
  readonly participants = input<CallParticipant[]>([]);

  /** Panel layout: 'list' (sidebar roster) or 'grid' (video thumbnail grid) */
  readonly layout = input<CallParticipantsLayout>('list');

  /** Whether to show the panel header with count */
  readonly showHeader = input<boolean>(true);

  /** Whether to show connection quality bars per participant */
  readonly showConnectionQuality = input<boolean>(false);

  /** Whether to show waiting-room participants separately */
  readonly showWaitingRoom = input<boolean>(true);

  /** Emitted when a participant item is clicked */
  readonly participantClick = output<CallParticipant>();

  /** Emitted when the "Admit" button is clicked for a waiting participant */
  readonly admitParticipant = output<CallParticipant>();

  protected readonly activeParticipants = computed(() => this.participants().filter((p) => !p.isWaiting));

  protected readonly waitingParticipants = computed(() => this.participants().filter((p) => p.isWaiting));

  protected readonly totalCount = computed(() => this.participants().length);

  protected getInitials(p: CallParticipant): string {
    if (p.initials) return p.initials;
    return p.name
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('');
  }

  protected getQuality(p: CallParticipant): 0 | 1 | 2 | 3 | 4 {
    return p.connectionQuality ?? 4;
  }
}
